"use client"

import React, { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from '@/utilities/ui'

export type Media = { url?: string; alt?: string } | null
export type Testimonial = {
  id: string
  name: string
  title?: string
  caption: string
  image?: Media
}

type Props = {
  testimonials: Testimonial[]
}

export default function TestimonialsRow({ testimonials }: Props) {
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])
  const [equalHeight, setEqualHeight] = useState<number | null>(null)

  // Measure tallest card after render and on resize
  useEffect(() => {
    const measure = () => {
      let maxH = 0
      for (const el of cardRefs.current) {
        if (el) {
          // Temporarily allow auto height for accurate measurement
          el.style.height = 'auto'
          const h = el.offsetHeight
          if (h > maxH) maxH = h
        }
      }
      setEqualHeight(maxH || null)
    }
    measure()
    const ro = new ResizeObserver(measure)
    for (const el of cardRefs.current) if (el) ro.observe(el)
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      ro.disconnect()
    }
  }, [testimonials])

  const items = useMemo(() => testimonials.slice(0, 3), [testimonials])

  return (
    <div className="flex flex-wrap gap-4 sm:gap-6 justify-center items-stretch">
      {items.map((t, idx) => {
        const img = t.image || null
        const imgUrl = img && typeof img === 'object' && 'url' in img ? (img.url as string) : undefined
        return (
          <div
            key={t.id}
            ref={(el) => {
              cardRefs.current[idx] = el
            }}
            className={cn(
              'w-full max-w-[300px] rounded-2xl px-5 sm:px-6 py-1',
              'bg-white/10 border-2',
              'border-[#029C90]/80',
              'flex flex-col'
            )}
            style={equalHeight ? { height: equalHeight } : undefined}
          >
            <div className="flex-1">
              <p className="text-[15px] sm:text-[16px] text-white leading-relaxed not-italic">"{t.caption}"</p>
            </div>
            <div className="mt-6 flex items-center justify-between gap-3 pt-2">
              {imgUrl ? (
                <Image
                  src={imgUrl}
                  alt={(img as any)?.alt || t.name}
                  width={64}
                  height={64}
                  className="rounded-full object-cover w-16 h-16"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted" />
              )}
              <div className="ml-2 flex-1 flex flex-col items-end text-right">
                <span className="text-[15px] font-semibold tracking-wider uppercase text-white">{t.name}</span>
                {t.title ? <span className="text-[13px] text-muted-foreground">{t.title}</span> : null}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
